/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const SelectionContext = createContext(null);

const STORAGE_KEY = 'aone_selected_child';
const COURSE_STORAGE_KEY_PREFIX = 'aone_selected_course_';

// Helper to get course storage key for a child
const getCourseStorageKey = (childId) => `${COURSE_STORAGE_KEY_PREFIX}${childId}`;

export function SelectionProvider({ children }) {
  const [selectedChild, setSelectedChild] = useState(() => {
    // Initialize from localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Counter to trigger incomplete count refetch across components
  const [incompleteCountVersion, setIncompleteCountVersion] = useState(0);

  // Counter to trigger unviewed suggestions count refetch across components
  const [unviewedCountVersion, setUnviewedCountVersion] = useState(0);

  // Persist selectedChild to localStorage
  useEffect(() => {
    if (selectedChild) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedChild));
    }
  }, [selectedChild]);

  // Persist selectedCourse to localStorage (per child)
  useEffect(() => {
    if (selectedChild?.id && selectedCourse?.id) {
      try {
        localStorage.setItem(getCourseStorageKey(selectedChild.id), String(selectedCourse.id));
      } catch {
        // Ignore storage errors
      }
    }
  }, [selectedChild?.id, selectedCourse?.id]);

  // Get saved course ID for a child
  const getSavedCourseId = useCallback((childId) => {
    try {
      const stored = localStorage.getItem(getCourseStorageKey(childId));
      return stored ? parseInt(stored, 10) : null;
    } catch {
      return null;
    }
  }, []);

  const selectChild = useCallback((child) => {
    setSelectedChild(child);
    setSelectedCourse(null); // Reset course when child changes (CourseSelector will restore from localStorage)
  }, []);

  const selectCourse = useCallback((course) => {
    setSelectedCourse(course);
  }, []);

  // Trigger refetch of incomplete counts across all components
  const triggerIncompleteCountRefetch = useCallback(() => {
    setIncompleteCountVersion(v => v + 1);
  }, []);

  // Trigger refetch of unviewed suggestions count across all components
  const triggerUnviewedCountRefetch = useCallback(() => {
    setUnviewedCountVersion(v => v + 1);
  }, []);

  const value = {
    selectedChild,
    selectedCourse,
    selectChild,
    selectCourse,
    getSavedCourseId,
    incompleteCountVersion,
    triggerIncompleteCountRefetch,
    unviewedCountVersion,
    triggerUnviewedCountRefetch,
  };

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
}

export function useSelection() {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
}
